import { EDITOR_TEMPLATE_SLUG_QUERY_KEY } from '@imockup/shared-config'
import { useRoute, useRouter } from 'vue-router'

import { useMockupEditorStore } from '@/stores/mockupEditor'
import { fetchWithBaUserToken } from '../../../shared/utils/fetchWithBaUserToken'
import { resolveMarketingApiOrigin } from '@/utils/marketingApiOrigin'

/**
 * 从路由 query 中解析模板 slug。
 *
 * @param raw - `route.query[key]` 原始值
 */
function parseTemplateSlugFromQuery(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim()
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string') return raw[0]!.trim()
  return ''
}

/**
 * 登录后消费 `?template=<slug>`：拉取列表解析 `template_file` 并载入工程 JSON。
 */
export function useEditorTemplateDeepLink() {
  const route = useRoute()
  const router = useRouter()
  const store = useMockupEditorStore()

  let inFlight = false
  let loadedSlug: string | null = null

  /**
   * 尝试根据当前 URL 载入深链模板（幂等：同一 slug 成功一次后跳过）。
   *
   * @returns 是否发起了载入流程（不代表成功）
   */
  async function tryConsumeTemplateParam(): Promise<boolean> {
    const slug = parseTemplateSlugFromQuery(route.query[EDITOR_TEMPLATE_SLUG_QUERY_KEY])
    if (!slug) return false
    if (loadedSlug === slug) return false
    if (inFlight) return false

    const origin = resolveMarketingApiOrigin()
    if (!origin) return false

    inFlight = true
    try {
      /** 经营销站 Nitro 同源拉取 JSON，避免浏览器直连 AnimockServer `/storage` 触发 CORS */
      const projectUrl = `${origin}/api/template/project?${new URLSearchParams({ slug }).toString()}`
      const fileRes = await fetchWithBaUserToken(projectUrl)
      if (fileRes.status === 404) {
        store.setStubMessage('未找到该模板或暂无工程文件。')
        const nextQuery = { ...route.query }
        delete nextQuery[EDITOR_TEMPLATE_SLUG_QUERY_KEY]
        await router.replace({ path: route.path, query: nextQuery })
        return true
      }
      if (!fileRes.ok) {
        store.setStubMessage('模板工程文件下载失败。')
        return true
      }
      const text = await fileRes.text()
      const ok = store.loadProjectFromJsonText(text)
      if (ok) {
        loadedSlug = slug
        const nextQuery = { ...route.query }
        delete nextQuery[EDITOR_TEMPLATE_SLUG_QUERY_KEY]
        await router.replace({ path: route.path, query: nextQuery })
      }
      return true
    } catch {
      store.setStubMessage('模板载入失败：网络异常。')
      return true
    } finally {
      inFlight = false
    }
  }

  return { tryConsumeTemplateParam }
}
